/* eslint-disable react/no-unescaped-entities */
import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  Crown,
  LogOut,
  Users,
  Hourglass,
  FileCheck,
  Gift,
  DollarSign,
  ArrowLeft,
  Mail,
  Phone,
  Calendar,
  RefreshCw,
  ShieldCheck,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "ql_token";

export function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}
export function setToken(t) {
  if (t) localStorage.setItem(TOKEN_KEY, t);
  else localStorage.removeItem(TOKEN_KEY);
}
function authHeaders() {
  const t = getToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
}

function formatErr(detail) {
  if (detail == null) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

/* =============== Login Page =============== */
export function AdminLoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // If already logged in, redirect to dashboard
  useEffect(() => {
    if (!getToken()) return;
    axios
      .get(`${API}/auth/me`, { headers: authHeaders() })
      .then(() => navigate("/admin", { replace: true }))
      .catch(() => setToken(null));
  }, [navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, {
        email: email.trim().toLowerCase(),
        password,
      });
      setToken(data.token);
      toast.success(`Welcome back, ${data.user.name || "Queen"} ♕`);
      navigate("/admin", { replace: true });
    } catch (err) {
      toast.error(formatErr(err?.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="admin-login-page"
      className="min-h-screen flex items-center justify-center p-6"
      style={{
        background:
          "linear-gradient(135deg, #2d0a40 0%, #4a1466 40%, #7d289b 80%, #d62288 100%)",
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-md"
      >
        <Link
          to="/"
          data-testid="admin-back-home"
          className="inline-flex items-center gap-2 text-sm text-[#f6e27a] hover:text-white mb-6 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Queenz Like Me
        </Link>

        <div className="bg-white rounded-3xl shadow-2xl p-8 sm:p-10">
          <div className="text-center mb-7">
            <img
              src="/logo-sm.png"
              alt="Queenz Like Me"
              className="mx-auto h-20 w-20 rounded-full ring-4 ring-[#d4af37]/40 shadow-lg mb-4"
            />
            <p className="font-script text-2xl gold-text mb-1">Admin</p>
            <h1 className="font-display text-3xl font-bold text-[#4a1466]">
              Sign in to the throne
            </h1>
            <p className="mt-2 text-sm text-[#5c4a54]">
              For Queenz Like Me leadership only.
            </p>
          </div>

          <form onSubmit={submit} className="space-y-5">
            <div>
              <label className="ql-label">Email</label>
              <input
                required
                type="email"
                data-testid="admin-email"
                className="ql-input"
                placeholder="you@queenzlikeme.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div>
              <label className="ql-label">Password</label>
              <input
                required
                type="password"
                data-testid="admin-password"
                className="ql-input"
                placeholder="Your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            <button
              type="submit"
              data-testid="admin-login-submit"
              disabled={loading}
              className="btn-primary w-full justify-center disabled:opacity-60"
            >
              {loading ? "Signing in…" : "Sign in"}
              {!loading && <Crown className="h-4 w-4" />}
            </button>
          </form>
        </div>

        <p className="mt-5 text-center text-xs text-white/70">
          Protected admin area · Queenz Like Me ♕
        </p>
      </motion.div>
    </div>
  );
}

/* =============== Admin Dashboard =============== */
export function AdminDashboard() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState("registrations");
  const [rows, setRows] = useState([]);
  const [loadingTab, setLoadingTab] = useState(false);
  const navigate = useNavigate();

  // initial auth check
  useEffect(() => {
    const token = getToken();
    if (!token) {
      setChecking(false);
      return;
    }
    axios
      .get(`${API}/auth/me`, { headers: authHeaders() })
      .then(({ data }) => {
        setUser(data);
        setChecking(false);
      })
      .catch(() => {
        setToken(null);
        setChecking(false);
      });
  }, []);

  const loadStats = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/stats`, {
        headers: authHeaders(),
      });
      setStats(data);
    } catch {
      // ignore
    }
  }, []);

  const loadTab = useCallback(async (which) => {
    setLoadingTab(true);
    try {
      const { data } = await axios.get(`${API}/admin/${which}`, {
        headers: authHeaders(),
      });
      setRows(data);
    } catch (err) {
      toast.error(formatErr(err?.response?.data?.detail) || "Failed to load");
    } finally {
      setLoadingTab(false);
    }
  }, []);

  useEffect(() => {
    if (user) {
      loadStats();
      loadTab(tab);
    }
  }, [user, tab, loadStats, loadTab]);

  if (checking) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ background: "#fff9fb" }}
      >
        <Crown className="h-8 w-8 text-[#d62288] animate-pulse" />
      </div>
    );
  }
  if (!user) return <Navigate to="/admin/login" replace />;

  const logout = () => {
    setToken(null);
    toast.success("Signed out");
    navigate("/admin/login", { replace: true });
  };

  const TABS = [
    { id: "registrations", label: "Registrations", icon: Users, color: "#d62288" },
    { id: "waitlist", label: "Waitlist", icon: Hourglass, color: "#d4af37" },
    { id: "agreements", label: "Agreements", icon: FileCheck, color: "#7d289b" },
    { id: "donations", label: "Donations", icon: DollarSign, color: "#2a9d8f" },
  ];

  return (
    <div
      data-testid="admin-dashboard"
      className="min-h-screen"
      style={{
        background:
          "linear-gradient(180deg, #fff5e0 0%, #fff9fb 30%, #fce6f0 100%)",
      }}
    >
      {/* Top bar */}
      <header className="sticky top-0 z-40 bg-white/85 backdrop-blur border-b border-[#f0e1e8]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2.5 group">
            <img
              src="/logo-sm.png"
              alt="Queenz Like Me"
              className="h-10 w-10 rounded-full ring-2 ring-[#d4af37]/50 shadow"
            />
            <div className="leading-none hidden sm:block">
              <span className="font-script text-xl gold-text">
                Queen<span className="font-display not-italic">z</span>
              </span>
              <span className="font-display text-sm font-semibold text-[#7d289b] ml-1">
                Admin
              </span>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#7d289b]/10 text-[#7d289b] text-xs font-semibold">
              <ShieldCheck className="h-3.5 w-3.5" /> {user.email}
            </span>
            <button
              onClick={logout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#d62288] text-white font-semibold text-sm hover:bg-[#b81a73] transition-colors"
            >
              <LogOut className="h-4 w-4" />
              Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-8 py-10">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <p className="font-script text-2xl gold-text">Welcome back, Queen</p>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-[#4a1466]">
            {user.name || "Lacey"}'s Dashboard
          </h1>
        </motion.div>

        {/* Stats */}
        <div className="mt-7 grid grid-cols-2 lg:grid-cols-5 gap-4">
          {[
            { k: "registrations", l: "Registrations", icon: Users, c: "#d62288" },
            { k: "waitlist", l: "Waitlist", icon: Hourglass, c: "#d4af37" },
            { k: "agreements", l: "Agreements", icon: FileCheck, c: "#7d289b" },
            { k: "donations_count", l: "Donations", icon: Gift, c: "#2a9d8f" },
            {
              k: "total_raised_usd",
              l: "Raised (USD)",
              icon: DollarSign,
              c: "#2a9d8f",
              money: true,
            },
          ].map((c) => (
            <div
              key={c.k}
              data-testid={`stat-${c.k}`}
              className="bg-white rounded-2xl p-5 border border-[#f0e1e8] shadow-sm"
            >
              <div className="flex items-center gap-2 text-xs uppercase tracking-wider font-semibold text-[#5c4a54]">
                <c.icon className="h-4 w-4" style={{ color: c.c }} />
                {c.l}
              </div>
              <p
                className="mt-2 font-display text-3xl font-bold"
                style={{ color: c.c }}
              >
                {stats
                  ? c.money
                    ? `$${(stats[c.k] || 0).toLocaleString()}`
                    : stats[c.k] ?? 0
                  : "—"}
              </p>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="mt-9 flex flex-wrap items-center gap-2 border-b border-[#f0e1e8]">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`tab-${t.id}`}
              className={`inline-flex items-center gap-2 px-4 py-3 text-sm font-semibold transition-all relative ${
                tab === t.id
                  ? "text-[#4a1466]"
                  : "text-[#5c4a54] hover:text-[#4a1466]"
              }`}
            >
              <t.icon
                className="h-4 w-4"
                style={{ color: tab === t.id ? t.color : "#8a7681" }}
              />
              {t.label}
              {tab === t.id && (
                <span
                  className="absolute bottom-0 left-3 right-3 h-[3px] rounded-t"
                  style={{ background: t.color }}
                />
              )}
            </button>
          ))}
          <button
            onClick={() => {
              loadStats();
              loadTab(tab);
              toast.success("Refreshed");
            }}
            data-testid="admin-refresh"
            className="ml-auto inline-flex items-center gap-2 px-3 py-2 rounded-full text-sm font-semibold text-[#7d289b] hover:bg-[#7d289b]/10"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </button>
        </div>

        {/* Table */}
        <div className="mt-6 bg-white rounded-2xl border border-[#f0e1e8] shadow-sm overflow-hidden">
          {loadingTab ? (
            <div className="p-10 text-center text-[#5c4a54]">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="p-10 text-center text-[#5c4a54]">
              <Crown className="h-7 w-7 mx-auto text-[#d4af37] mb-2" />
              Nothing here yet — new entries will appear automatically.
            </div>
          ) : (
            <RecordsTable tab={tab} rows={rows} />
          )}
        </div>
      </main>
    </div>
  );
}

/* =============== Tables =============== */
function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function GroupBadge({ group }) {
  const map = {
    royal_crowns: { l: "Royal Crowns", c: "#ff6fa5" },
    bold_queens: { l: "Bold Queenz", c: "#9b59b6" },
    fearless_queens: { l: "Fearless Queenz", c: "#e89a2a" },
  };
  const v = map[group];
  if (!v) return <span className="text-[#5c4a54]">—</span>;
  return (
    <span
      className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold"
      style={{ background: `${v.c}1f`, color: v.c, border: `1px solid ${v.c}55` }}
    >
      <Crown className="h-3 w-3" />
      {v.l}
    </span>
  );
}

function RecordsTable({ tab, rows }) {
  if (tab === "registrations") {
    return (
      <Table headers={["Date", "Daughter", "DOB", "Age", "Group", "Shirt", "Parent", "Contact", "Message"]}>
        {rows.map((r) => (
          <tr key={r.id} className="border-t border-[#f0e1e8] hover:bg-[#fff5fa]">
            <Td><Calendar className="inline h-3.5 w-3.5 mr-1 text-[#7d289b]" />{fmtDate(r.created_at)}</Td>
            <Td bold>{r.child_first_name}</Td>
            <Td>{r.child_dob || "—"}</Td>
            <Td>{r.child_age}</Td>
            <Td><GroupBadge group={r.age_group} /></Td>
            <Td>{r.shirt_size || "—"}</Td>
            <Td>
              {r.parent_name}
              {r.signature_name && (
                <span className="block text-[10px] text-[#7d289b] font-semibold">
                  ✍ Signed: {r.signature_name}
                </span>
              )}
            </Td>
            <Td>
              <a href={`mailto:${r.parent_email}`} className="block text-[#d62288] hover:underline"><Mail className="inline h-3 w-3 mr-1" />{r.parent_email}</a>
              <a href={`tel:${r.parent_phone}`} className="block text-[#5c4a54] hover:underline"><Phone className="inline h-3 w-3 mr-1" />{r.parent_phone}</a>
            </Td>
            <Td className="max-w-xs truncate">{r.message || "—"}</Td>
          </tr>
        ))}
      </Table>
    );
  }
  if (tab === "waitlist") {
    return (
      <Table headers={["Date", "Daughter", "Age", "Group", "Parent", "Contact", "Notes"]}>
        {rows.map((r) => (
          <tr key={r.id} className="border-t border-[#f0e1e8] hover:bg-[#fff5fa]">
            <Td><Calendar className="inline h-3.5 w-3.5 mr-1 text-[#7d289b]" />{fmtDate(r.created_at)}</Td>
            <Td bold>{r.child_first_name}</Td>
            <Td>{r.child_age}</Td>
            <Td><GroupBadge group={r.age_group} /></Td>
            <Td>{r.parent_name}</Td>
            <Td>
              <a href={`mailto:${r.parent_email}`} className="block text-[#d62288] hover:underline"><Mail className="inline h-3 w-3 mr-1" />{r.parent_email}</a>
              {r.parent_phone && <a href={`tel:${r.parent_phone}`} className="block text-[#5c4a54] hover:underline"><Phone className="inline h-3 w-3 mr-1" />{r.parent_phone}</a>}
            </Td>
            <Td className="max-w-xs truncate">{r.notes || "—"}</Td>
          </tr>
        ))}
      </Table>
    );
  }
  if (tab === "agreements") {
    return (
      <Table headers={["Signed", "Participant", "Parent", "Email", "Emergency", "Media Release"]}>
        {rows.map((r) => (
          <tr key={r.id} className="border-t border-[#f0e1e8] hover:bg-[#fff5fa]">
            <Td><Calendar className="inline h-3.5 w-3.5 mr-1 text-[#7d289b]" />{fmtDate(r.signed_at)}</Td>
            <Td bold>{r.participant_name}</Td>
            <Td>{r.parent_name}</Td>
            <Td><a href={`mailto:${r.parent_email}`} className="text-[#d62288] hover:underline">{r.parent_email}</a></Td>
            <Td>{r.emergency_contact}</Td>
            <Td>
              <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${r.media_release ? 'bg-[#2a9d8f]/15 text-[#2a9d8f] border border-[#2a9d8f]/40' : 'bg-[#5c4a54]/10 text-[#5c4a54] border border-[#5c4a54]/30'}`}>
                {r.media_release ? "Granted" : "Not granted"}
              </span>
            </Td>
          </tr>
        ))}
      </Table>
    );
  }
  if (tab === "donations") {
    return (
      <Table headers={["Date", "Amount", "Type", "Donor", "Email", "Status"]}>
        {rows.map((r) => {
          const m = r.metadata || {};
          return (
            <tr key={r.session_id} className="border-t border-[#f0e1e8] hover:bg-[#fff5fa]">
              <Td><Calendar className="inline h-3.5 w-3.5 mr-1 text-[#7d289b]" />{fmtDate(r.created_at)}</Td>
              <Td bold>${Number(r.amount).toLocaleString()}</Td>
              <Td>
                <span className="text-xs font-semibold text-[#7d289b]">{m.package_label || "—"}</span>
                <span className="block text-[10px] uppercase tracking-wider text-[#5c4a54]">{m.frequency === "monthly" ? "Monthly" : "One-time"}</span>
              </Td>
              <Td>{m.donor_name || "Anonymous"}</Td>
              <Td>{m.donor_email ? <a href={`mailto:${m.donor_email}`} className="text-[#d62288] hover:underline">{m.donor_email}</a> : "—"}</Td>
              <Td>
                <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-[#2a9d8f]/15 text-[#2a9d8f] border border-[#2a9d8f]/40">
                  PAID
                </span>
              </Td>
            </tr>
          );
        })}
      </Table>
    );
  }
  return null;
}

function Table({ headers, children }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm text-left">
        <thead className="bg-[#fce6f0]/60 text-[#4a1466]">
          <tr>
            {headers.map((h) => (
              <th key={h} className="px-4 py-3 font-display font-semibold tracking-wide uppercase text-[11px]">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

function Td({ children, className = "", bold = false }) {
  return (
    <td
      className={`px-4 py-3 text-[#1a0f14] ${bold ? "font-semibold" : ""} ${className}`}
    >
      {children}
    </td>
  );
}
